import { MutableRefObject, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Box, Skeleton, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { useVirtualizer } from "@tanstack/react-virtual"
import { UseQueryResult } from "@tanstack/react-query"

import { FormCard } from "../../../../../../shared/components/forms/FormCard"
import { LocalizedEnum, LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import type { TranslationKey } from "../../../../../localization/translations"
import { ResourceType } from "../../../../admin/organization/resources/resourcesTypes"

import { cssVars, ResourceEntry } from "./resourceDashboardTypes"

const ROW_HEIGHT_PX = 56
const SCROLL_MAX_HEIGHT = "30rem"
const HIGHLIGHT_DURATION_MS = 2500
const SKELETON_ROW_COUNT = 6

export type ResourceUsageSectionHandle = Readonly<{
    scrollToResource: (resourceId: number) => void
}>

// A single column of the per-type table. `renderCell`/`renderSkeleton` keep the section metric-agnostic
// so occupancy, bookings, … can each supply their own cells while sharing the virtualization shell.
export type UsageColumn<TEntry> = Readonly<{
    id: string
    headerId: TranslationKey
    headerFallback: string
    // CSS grid track for this column, e.g. "minmax(8rem, 1.6fr)" or "10rem".
    gridTrack: string
    align?: "start" | "end"
    renderCell: (entry: TEntry, resourceType: ResourceType) => ReactNode
    renderSkeleton: () => ReactNode
}>

type ResourceUsageSectionProps<TEntry extends ResourceEntry> = Readonly<{
    resourceType: ResourceType
    queryResult: UseQueryResult<TEntry[]>
    columns: ReadonlyArray<UsageColumn<TEntry>>
    // Populated with the imperative handle so a parent (e.g. the summary bar chart) can scroll to a row.
    handleRef?: MutableRefObject<ResourceUsageSectionHandle | null>
    emptyMessageId: TranslationKey
    emptyMessageFallback: string
}>

const useStyles = makeStyles()((theme) => ({
    heading: {
        marginBottom: ".75rem",
        color: theme.palette.tailwind.slate[800],
    },
    // Fixed header sits ABOVE its own scroll area (not sticky inside it), so scrolling rows are simply
    // clipped at the scroll box's top edge — nothing renders above the header.
    headerRow: {
        display: "grid",
        gridTemplateColumns: "var(--grid-columns)",
        gap: "1rem",
        alignItems: "center",
        padding: ".5rem 1rem",
        borderBottom: `1px solid ${theme.palette.tailwind.slate[200]}`,
        color: theme.palette.tailwind.slate[400],
        fontWeight: 600,
        fontSize: ".75rem",
        textTransform: "uppercase",
        letterSpacing: ".04em",
    },
    headerCellEnd: {
        justifySelf: "end",
    },
    scrollBox: {
        position: "relative",
        maxHeight: SCROLL_MAX_HEIGHT,
        overflowY: "auto",
    },
    spacer: {
        position: "relative",
        width: "100%",
        height: "var(--spacer-height)",
    },
    row: {
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: `${ROW_HEIGHT_PX / 16}rem`,
        display: "grid",
        gridTemplateColumns: "var(--grid-columns)",
        gap: "1rem",
        alignItems: "center",
        padding: "0 1rem",
        borderBottom: `1px solid ${theme.palette.tailwind.slate[100]}`,
        transform: "translateY(var(--row-offset))",
    },
    rowHighlight: {
        backgroundColor: theme.palette.tailwind.amber[100],
        transition: "background-color .4s ease",
    },
    skeletonRow: {
        display: "grid",
        gridTemplateColumns: "var(--grid-columns)",
        gap: "1rem",
        alignItems: "center",
        height: `${ROW_HEIGHT_PX / 16}rem`,
        padding: "0 1rem",
        borderBottom: `1px solid ${theme.palette.tailwind.slate[100]}`,
    },
    // Direct grid children; `min-width: 0` lets a track shrink to its fr share so inner cells
    // (e.g. an ellipsised name) clip instead of overflowing.
    cell: {
        minWidth: 0,
    },
    cellEnd: {
        display: "flex",
        justifyContent: "flex-end",
        textAlign: "right",
    },
    empty: {
        color: theme.palette.tailwind.slate[500],
        textAlign: "center",
        padding: "2rem 0",
    },
}))

export function ResourceUsageSection<TEntry extends ResourceEntry>({
    resourceType,
    queryResult,
    columns,
    handleRef,
    emptyMessageId,
    emptyMessageFallback,
}: ResourceUsageSectionProps<TEntry>) {
    const { classes, cx } = useStyles()

    const cardRef = useRef<HTMLDivElement>(null)
    const scrollRef = useRef<HTMLDivElement>(null)
    const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout>>()
    const [highlightedResourceId, setHighlightedResourceId] = useState<number | undefined>()

    const rows = useMemo(() => queryResult.data ?? [], [queryResult.data])

    const gridColumns = useMemo(() => columns.map((column) => column.gridTrack).join(" "), [columns])
    const gridStyle = useMemo(() => cssVars({ "--grid-columns": gridColumns }), [gridColumns])

    const rowVirtualizer = useVirtualizer({
        count: rows.length,
        getScrollElement: () => scrollRef.current,
        estimateSize: () => ROW_HEIGHT_PX,
        overscan: 10,
    })

    const scrollToResource = useCallback(
        (resourceId: number) => {
            cardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })

            const index = rows.findIndex((row) => row.resource.id === resourceId)
            if (index >= 0) {
                // Let the page scroll settle before scrolling the (independent) list viewport.
                setTimeout(() => rowVirtualizer.scrollToIndex(index, { align: "center" }), 150)
            }

            setHighlightedResourceId(resourceId)
            if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current)
            highlightTimeoutRef.current = setTimeout(() => setHighlightedResourceId(undefined), HIGHLIGHT_DURATION_MS)
        },
        [rows, rowVirtualizer],
    )

    useEffect(() => {
        if (!handleRef) return
        handleRef.current = { scrollToResource }
        return () => {
            handleRef.current = null
        }
    }, [handleRef, scrollToResource])

    const totalSize = rowVirtualizer.getTotalSize()
    const spacerStyle = useMemo(() => cssVars({ "--spacer-height": `${totalSize}px` }), [totalSize])

    const header = (
        <Box className={classes.headerRow} style={gridStyle}>
            {columns.map((column) => (
                <LocalizedStrict key={column.id} id={column.headerId}>
                    <span className={cx(column.align === "end" && classes.headerCellEnd)}>{column.headerFallback}</span>
                </LocalizedStrict>
            ))}
        </Box>
    )

    const renderContent = () => {
        if (queryResult.isInitialLoading) {
            return (
                <>
                    {header}
                    {Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
                        <Box key={index} className={classes.skeletonRow} style={gridStyle}>
                            {columns.map((column) => (
                                <Box
                                    key={column.id}
                                    className={cx(classes.cell, column.align === "end" && classes.cellEnd)}
                                >
                                    {column.renderSkeleton()}
                                </Box>
                            ))}
                        </Box>
                    ))}
                </>
            )
        }

        if (rows.length === 0) {
            return (
                <LocalizedStrict id={emptyMessageId}>
                    <Typography variant="body2" className={classes.empty}>
                        {emptyMessageFallback}
                    </Typography>
                </LocalizedStrict>
            )
        }

        return (
            <>
                {header}
                <Box ref={scrollRef} className={classes.scrollBox}>
                    <Box className={classes.spacer} style={spacerStyle}>
                        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                            const entry = rows[virtualRow.index]
                            const rowStyle = cssVars({
                                "--grid-columns": gridColumns,
                                "--row-offset": `${virtualRow.start}px`,
                            })

                            return (
                                <Box
                                    key={virtualRow.index}
                                    className={cx(
                                        classes.row,
                                        entry.resource.id === highlightedResourceId && classes.rowHighlight,
                                    )}
                                    style={rowStyle}
                                >
                                    {columns.map((column) => (
                                        <Box
                                            key={column.id}
                                            className={cx(classes.cell, column.align === "end" && classes.cellEnd)}
                                        >
                                            {column.renderCell(entry, resourceType)}
                                        </Box>
                                    ))}
                                </Box>
                            )
                        })}
                    </Box>
                </Box>
            </>
        )
    }

    return (
        <Box ref={cardRef}>
            <FormCard>
                <LocalizedEnum base="resource-dashboard-type" enumValue={resourceType}>
                    <Typography variant="h6" className={classes.heading}>
                        {resourceType}
                    </Typography>
                </LocalizedEnum>
                {renderContent()}
            </FormCard>
        </Box>
    )
}
